import React, { useState } from 'react';
import { FolderOpen } from 'lucide-react';
import Select from 'react-select';
import { CATEGORIESYAHOO, CategoryYahoo } from '../../../utils/categories';
import type { BulkUpdates } from '../../../types/bulk';
import clsx from 'clsx';
import { BulkApplyButton, BulkFieldRow } from '../BulkFieldRow';

interface BulkCategoryFieldProps {
  onUpdate: (updates: BulkUpdates) => void;
}

const selectStyles = {
  control: {
    base: "flex border bg-gray-50 rounded-md bg-input hover:cursor-pointer min-w-[100px] !min-h-[30px] w-[100%] h-[100%] text-foreground",
    focus: "border border-primary shadow-[0_0_0_2px_rgba(var(--primary),0.3)]",
    nonFocus: "border-border"
  },
  option: {
    base: "hover:cursor-pointer px-3 py-2 rounded flex gap-2 items-center hover:bg-muted !text-sm",
    focus: "",
    selected: "bg-[rgba(var(--primary),0.15)] hover:bg-[rgba(var(--primary),0.15)] text-primary font-bold"
  }
};

const classNames = {
  control: ({ isFocused }: { isFocused: boolean }) =>
    clsx(
      isFocused ? selectStyles.control.focus : selectStyles.control.nonFocus,
      selectStyles.control.base
    ),
  option: ({ isSelected, isFocused }: { isSelected: boolean; isFocused: boolean }) =>
    clsx(
      selectStyles.option.base,
      isSelected ? selectStyles.option.selected : "",
      isFocused ? selectStyles.option.focus : ""
    ),
  menu: () => "p-1 mt-1 bg-card rounded-md shadow-md bg-gray-100",
  placeholder: () => "text-placeholder pl-1 text-sm",
  input: () => "text-foreground pl-1",
  valueContainer: () => "p-1 gap-1",
  singleValue: () => "pl-1",
  multiValue: () => "flex bg-[rgba(var(--primary),0.15)] p-0 pl-2 rounded-sm text-xs overflow-hidden",
  multiValueLabel: () => "py-1",
  multiValueRemove: () => "bg-[rgba(var(--primary),0.1)] hover:bg-[rgba(var(--primary),0.2)] text-primary px-1 ml-2",
  indicatorsContainer: () => "p-1 gap-1",
  clearIndicator: () => "text-muted-foreground px-1 hover:text-error",
  indicatorSeparator: () => "bg-border",
  dropdownIndicator: () => "p-1 hover:bg-muted text-muted-foreground rounded-md hover:text-foreground",
  groupHeading: () => "ml-3 mt-2 mb-1 text-muted-foreground text-sm",
  noOptionsMessage: () => "text-placeholder py-3"
};

const FormatOptionLabel = React.memo(({ option }: { option: CategoryYahoo }) => (
  <><div>{option.label}</div><div className='text-xs'>{option.path}</div></>
));

export function BulkCategoryField({ onUpdate }: BulkCategoryFieldProps) {
  const [searchInput, setSearchInput] = useState('');
  const [debouncedInput, setDebouncedInput] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<CategoryYahoo | null>(null);

  React.useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedInput(searchInput);
    }, 300);

    return () => {
      clearTimeout(handler);
    };
  }, [searchInput]);

  const filteredOptions = React.useMemo(() => {
    if (debouncedInput.length < 1) return [];

    let searchTerms: string[] = [];

    if (debouncedInput.includes(',')) {
      searchTerms = debouncedInput.toLowerCase().split(',').map(term => term.trim()).filter(Boolean);
    } else {
      searchTerms = debouncedInput.toLowerCase().split(/\s+/).filter(Boolean);
    }

    if (searchTerms.length === 0) return [];

    const results = CATEGORIESYAHOO.filter(option => {
      const path = String(option.path || '').toLowerCase();
      const label = String(option.label || '').toLowerCase();

      if (searchTerms.length > 1) {
        return searchTerms.every(term => path.includes(term) || label.includes(term));
      }

      const term = searchTerms[0];
      return path.includes(term) || label.includes(term);
    });

    return results
      .sort((a, b) => {
        const aTotalLength = String(a.label || '').length + String(a.path || '').length;
        const bTotalLength = String(b.label || '').length + String(b.path || '').length;
        return aTotalLength - bTotalLength;
      })
      .slice(0, 100);
  }, [debouncedInput]);

  const handleApply = () => {
    if (selectedCategory) {
      onUpdate({ categoryYahoo: selectedCategory.value });
      setSelectedCategory(null);
      setSearchInput('');
    }
  };

  const noOptionsMessage = ({ inputValue }: { inputValue: string }) => {
    if (inputValue.length < 1) {
      return <div className="text-sm font-medium">検索には１文字以上入力してください</div>;
    }
    return "カテゴリが見つかりません";
  };

  return (
    <BulkFieldRow
      icon={<FolderOpen className="w-5 h-5 text-gray-500 shrink-0" />}
      label="カテゴリを変更"
      footer={
        <p className="mt-2 text-xs text-gray-500 md:ml-48 lg:ml-56">
          検索のヒント: 1文字以上入力すると検索が始まります。カンマまたはスペースで複数のキーワードを指定できます。
        </p>
      }
    >
      <div className="min-w-0 flex-1">
        <Select
          unstyled
          name="categoryYahoo"
          options={filteredOptions}
          value={selectedCategory}
          formatOptionLabel={(option) => (
            <FormatOptionLabel option={option} />
          )}
          classNames={classNames}
          placeholder="カテゴリを入力"
          noOptionsMessage={noOptionsMessage}
          isClearable={true}
          onChange={setSelectedCategory}
          onInputChange={setSearchInput}
          isSearchable={true}
          filterOption={() => true}
          isLoading={debouncedInput.length >= 1 && filteredOptions.length === 0}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          components={{
            DropdownIndicator: () => (
              <div className="p-1 text-gray-400">
                {isFocused && !searchInput && (
                  <div className="absolute top-full left-0 right-0 mt-1 p-2 bg-white border border-gray-200 rounded-md shadow-md z-10">
                    <div className="text-sm font-medium text-blue-600">検索には１文字以上入力してください</div>
                  </div>
                )}
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="6 9 12 15 18 9"></polyline>
                </svg>
              </div>
            )
          }}
        />
      </div>
      <BulkApplyButton disabled={!selectedCategory} onClick={handleApply} />
    </BulkFieldRow>
  );
}
